import {useState} from 'react';

import type {ReactElement} from 'react';

const asset = (name: string): string => new URL(`../../assets/${name}`, import.meta.url).href;

interface Layer {
  backdrop?: 'sheet' | 'zoom';
  flip?: boolean;
  caption: string;
  image: string;
  position?: string;
}

const layers: Layer[] = [
  {backdrop: 'sheet', caption: 'Character study · Illustrative', image: asset('character-maya-reference.png')},
  {caption: 'Illustrative', image: asset('character-city-continuity.png'), position: '60% 40%'},
  {backdrop: 'zoom', caption: 'Illustrative', image: asset('approval-entrance-take-01.png'), position: '50% 0%'},
  {caption: 'Illustrative story example', image: asset('approval-entrance-take-02.png'), position: '50% 25%'},
  {caption: 'Illustrative story example', image: asset('approval-entrance-take-01.png'), position: '50% 25%'},
  {caption: 'Illustrative', flip: true, image: asset('character-memories.png'), position: '30% 40%'}
];

const introCopy = 'Build a virtual character around a point of view: what she wants, what she fears, what she knows, '
  + 'and what she has already lived through. Then give each encounter a consequence she can carry forward.';

type Choice = 'leaves' | 'stays';

const outcomes: Record<Choice, {label: string; rows: [string, string][]}> = {
  leaves: {
    label: 'He leaves without explaining',
    rows: [
      ['She feels', 'Hope gives way to disappointment. She holds her expression steady rather than making a scene.'],
      ['She does', 'Her almost-smile fades. She releases the door and watches him leave instead of following.'],
      ['She remembers', 'She offered an opening, and he walked away without answering.'],
      ['Next scene', 'She keeps her distance and waits for him to make the first move.']
    ]
  },
  stays: {
    label: 'He stays to explain',
    rows: [
      ['She feels', 'Relief arrives before trust. She wants to believe him, while the earlier disappointment still matters.'],
      ['She does', 'Her grip loosens. She opens the door wider, but lets him speak first.'],
      ['She remembers', 'He returned and stayed when she gave him a chance to speak.'],
      ['Next scene', 'She asks a direct question instead of preparing to leave.']
    ]
  }
};

const choices: Choice[] = ['stays', 'leaves'];

const steps = [
  ['Start with the person', 'Save her description and reference: goals, temperament, history, relationships.'],
  ['Record what changed', 'After a shot, note the experience, emotion, and what she now knows.'],
  ['Carry it forward', 'Later shots include her past in order, so future events never leak backward.'],
  ['Shape the performance', 'Turn context into direction: attention, posture, timing, and choice.']
];

const footnote = 'Today, you write these records and they guide shot prompts. This is a story you can direct, '
  + 'not an autonomous simulation.';

const rowLabel = 'text-[.68rem] font-semibold tracking-[.16em] text-violet-200 uppercase';
const rowText = 'mt-0.5 text-sm leading-snug text-white/90';
const pillBase = 'block w-full cursor-pointer rounded-full px-5 py-3 text-left text-base font-medium text-white '
  + 'transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-300';
const stageClass = 'relative isolate overflow-hidden rounded-3xl border border-white/10 bg-[#0b0b10] '
  + 'px-4 pt-80 pb-5 shadow-[0_40px_100px_#000c] lg:h-180 lg:p-0';
const stageOverlay = 'pointer-events-none absolute inset-0 z-10 bg-[linear-gradient(0deg,#0b0b10ee_45%,#0b0b1033)] '
  + 'lg:bg-[linear-gradient(90deg,#0b0b10dd_0%,#0b0b1088_38%,transparent_62%),linear-gradient(0deg,#0b0b1077,transparent_25%)]';
const photo = 'absolute inset-0 size-full scale-[1.04] object-cover transition-transform duration-[7000ms] '
  + 'group-data-[active=true]/layer:scale-100';
const backdrops: Record<string, string> = {
  sheet: 'blur-[40px] brightness-60 saturate-120 scale-130! group-data-[active=true]/layer:scale-130!',
  zoom: 'origin-[50%_10%] lg:translate-x-[10%] scale-[1.15]! group-data-[active=true]/layer:scale-110!'
};

const Reveal = ({children, index}: {children: ReactElement; index: number}): ReactElement => (
  <div
    className="translate-y-2 opacity-0 transition duration-500 group-data-[open=true]/item:translate-y-0 group-data-[open=true]/item:opacity-100"
    style={{transitionDelay: `${120 + (index * 70)}ms`}}>
    {children}
  </div>
);

const Row = ({label, text}: {label: string; text: string}): ReactElement => (
  <div><p className={rowLabel}>{label}</p><p className={rowText}>{text}</p></div>
);

const sections = ['Who she is', 'What she notices', 'What shows', 'What happens next?', 'Build her continuity'];

export const CharacterShowcase = (): ReactElement => {
  const [selected, setSelected] = useState(3);
  const [choice, setChoice] = useState<Choice>('stays');
  const activeLayer = [0, 1, 2, choice === 'stays' ? 3 : 4, 5][selected];

  const bodies: ReactElement[] = [
    <p className={`${rowText} opacity-0 transition-opacity duration-500 group-data-[open=true]/item:opacity-100`} key="who">
      Curious, careful, and reluctant to leave a question unanswered. Her traits stay recognizable
      even as relief or disappointment changes how she responds.
    </p>,
    <p className={`${rowText} opacity-0 transition-opacity duration-500 group-data-[open=true]/item:opacity-100`} key="notices">
      She notices whether he stops for her before she notices the rain. An earlier broken promise
      makes his hesitation feel significant.
    </p>,
    <p className={`${rowText} opacity-0 transition-opacity duration-500 group-data-[open=true]/item:opacity-100`} key="shows">
      Hope and hurt become a held gaze, a hand on the door, a delayed answer, or a step closer —
      never a generic “sad” or “happy”.
    </p>,
    <div className="space-y-3" key="next">
      <p className="text-sm leading-snug text-white/70">
        She remembers he once left without saying goodbye. Now he is here again. Choose what happens.
      </p>
      <div aria-label="Choose what happens" className="grid grid-cols-2 gap-2" role="group">
        {choices.map((key) => (
          <button
            aria-pressed={choice === key}
            className="cursor-pointer rounded-full border border-white/25 px-4 py-2 text-sm text-white transition hover:border-violet-300 aria-pressed:border-violet-300 aria-pressed:bg-violet-300 aria-pressed:text-[#1b1029] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-300"
            key={key}
            onClick={() => setChoice(key)}
            type="button">{outcomes[key].label}</button>
        ))}
      </div>
      <div className="space-y-2.5 border-t border-white/15 pt-3" key={choice}>
        {outcomes[choice].rows.map(([label, text], index) => (
          <Reveal index={index + 1} key={label}><Row label={label} text={text} /></Reveal>
        ))}
      </div>
    </div>,
    <div className="space-y-2.5" key="build">
      {steps.map(([label, text], index) => (
        <Reveal index={index} key={label}>
          <div className="flex gap-3">
            <span className="mt-0.5 font-mono text-xs text-violet-200">0{index + 1}</span>
            <Row label={label} text={text} />
          </div>
        </Reveal>
      ))}
      <p className="border-t border-white/15 pt-3 text-xs leading-snug text-white/60">{footnote}</p>
    </div>
  ];

  return (
    <section aria-labelledby="characters-heading" className="showcase-section" id="characters-showcase">
      <header className="mb-10 max-w-208">
        <p className="showcase-eyebrow">Identity → Experience → Memory → Next moment</p>
        <h2 className="text-4xl leading-[1.08] font-semibold tracking-tight lg:text-6xl" id="characters-heading">
          A moment changes what she brings to the next one.
        </h2>
        <p className="mt-5 max-w-184 leading-relaxed text-[#c5c1cd]">{introCopy}</p>
      </header>
      <div className={stageClass}>
        <div aria-hidden="true" className={stageOverlay} />
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-80 lg:inset-0 lg:h-auto">
          {layers.map(({backdrop, flip, image, position}, index) => (
            <div
              className="group/layer absolute inset-0 overflow-hidden opacity-0 transition-opacity duration-700 data-[active=true]:opacity-100"
              data-active={activeLayer === index}
              key={image + index}>
              <img
                alt=""
                className={`${photo} ${backdrop ? backdrops[backdrop] : ''} ${flip ? '[transform:scaleX(-1)]' : ''}`}
                loading={index === 3 ? 'eager' : 'lazy'}
                src={image}
                style={{objectPosition: position}} />
              {backdrop === 'sheet' && (
                <img
                  alt=""
                  className="absolute top-[5%] left-1/2 aspect-[2/3] h-[70%] -translate-x-1/2 rounded-xl object-cover shadow-[0_30px_80px_#000a] lg:top-[8%] lg:left-[calc(50%+14.125rem)] lg:h-[84%]"
                  loading="lazy"
                  src={image} />
              )}
            </div>
          ))}
        </div>
        <div className="relative z-20 flex flex-col justify-center gap-2 lg:absolute lg:inset-y-6 lg:left-8 lg:w-105">
          {sections.map((label, index) => {
            const open = selected === index;
            return (
              <div className="group/item" data-open={open} key={label}>
                <div className="rounded-3xl bg-white/10 backdrop-blur-xl transition-colors duration-300 hover:bg-white/15 group-data-[open=true]/item:bg-[#2a1d3f]/80 group-data-[open=true]/item:ring-1 group-data-[open=true]/item:ring-violet-300/50">
                  <button
                    aria-controls={`character-panel-${index}`}
                    aria-expanded={open}
                    className={pillBase}
                    onClick={() => setSelected(index)}
                    type="button">{label}</button>
                  <div
                    aria-hidden={!open}
                    className="invisible grid grid-rows-[0fr] transition-[grid-template-rows,visibility] duration-500 group-data-[open=true]/item:visible group-data-[open=true]/item:grid-rows-[1fr]"
                    id={`character-panel-${index}`}
                    role="region">
                    <div className="min-h-0 overflow-hidden px-5">
                      <div className="pb-4">{bodies[index]}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="absolute right-6 bottom-5 z-20 hidden text-[.7rem] tracking-[.18em] text-white/80 uppercase lg:block">
          {layers[activeLayer].caption}
        </p>
      </div>
    </section>
  );
};
